import React, { useState } from 'react';
import { useStaticQuery, graphql, Link } from 'gatsby';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { TextField } from '@material-ui/core';

import { GatsbySanityImageFluid } from '../types';
import { HIDDEN_FEATURES } from '../constants';
import { useUpdateUrlFromFilters, usePagination, useOnLoadEffect } from '../utils/hooks';
import {
  mapBy,
  flatten,
  sortBy,
  uniq,
  uniqBy,
  convertRToSuperscript,
  getProblemCommonAndOrScientificName,
} from '../utils/utils';
import Error404Page from './404';
import SEO from '../components/SEO';
import PageLayout from '../components/PageLayout';
import SearchBox, { MatchField } from '../components/SearchBox';
import ProductCard from '../components/ProductCard';
import GenericLink from '../components/GenericLink';

import * as filterStyles from '../components/FilterBox.module.scss';
import * as styles from './products.module.scss';

type ID = string;

interface ProductType {
  _id: string;
  name: string;
  slug: {
    current: string;
  };
  group: {
    _id: string;
    name: string;
    otherProductType?: {
      _id: string;
    };
  };
}
interface ActiveSubstance {
  _id: string;
  name: string;
  slug: {
    current: string;
  };
}

export interface Crop {
  _id: string;
  name: string;
  slug: {
    current: string;
  };
  group: {
    _id: string;
    name: string;
  };
}

type Problem = {
  _id: string;
  slug: {
    current: string;
  };

  group: {
    _id: string;
    name: string;
    category: {
      _id: string;
    };
  };
} & (
  | {
      name: string;
      scientificName?: null;
    }
  | {
      name?: null;
      scientificName: string;
    }
  | {
      name: string;
      scientificName: string;
    }
);

interface ProblemCategory {
  _id: string;
  name: string;
  slug: {
    current: string;
  };
}

interface QueryData {
  allSanityProduct: {
    nodes: Array<Product>;
  };
  allSanityProductType: {
    nodes: Array<ProductType>;
  };
  allSanityActiveSubstance: {
    nodes: Array<ActiveSubstance>;
  };
  allSanityCrop: {
    nodes: Array<Crop>;
  };
  allSanityProblem: {
    nodes: Array<Problem>;
  };
  allSanityProblemCategory: {
    nodes: Array<ProblemCategory>;
  };
  sanityProductsListPage: {
    disclaimer?: string;
    disclaimerArticle?: {
      title: string;
      slug: {
        current: string;
      };
    };
  };
}

export interface Product {
  _id: ID;
  unlisted?: boolean;
  name: string;
  slug: {
    current: string;
  };
  logoImage?: {
    asset: {
      fluid: GatsbySanityImageFluid;
    };
  };
  image?: {
    asset: {
      fluid: GatsbySanityImageFluid;
    };
  };
  type: {
    _id: ID;
    brand: 'ascenza' | 'blexia' | null;
    color: string;
    name: string;
  };
  activeSubstanceInfos?: Array<{
    activeSubstance: {
      _id: ID;
      name: string;
    };
  }>;
  problemLabel?: string;
  problemDescription?: string;
  cropDescription?: string;
  applications?: Array<{
    crop: {
      _id: ID;
      name: string;
    };
    cropApplications?: Array<{
      problem: {
        _id: ID;
        name: string;
      };
    }>;
  }>;
}

interface IndexedProduct {
  id: ID;
  name: string;
  typeId: string;
  activeSubstanceIds: Array<string>;
  cropIds: Array<string>;
  problemIds: Array<string>;
}

type Filters = Record<'type' | 'activeSubstance' | 'crop' | string, ID | null>;

function indexProducts(products: Array<Product>): Array<IndexedProduct> {
  return products.map(product => ({
    id: product._id,
    name: product.name,
    typeId: product.type._id,
    activeSubstanceIds: product.activeSubstanceInfos
      ? product.activeSubstanceInfos.map(
          activeSubstanceInfo => activeSubstanceInfo.activeSubstance._id,
        )
      : [],
    cropIds: product.applications?.map(application => application.crop._id) || [],
    problemIds: uniq(
      flatten(
        product.applications?.map(application =>
          application.cropApplications
            ? application.cropApplications
                .filter(cropApplication => !!cropApplication.problem)
                .map(cropApplication => cropApplication.problem._id)
            : [],
        ) || [],
      ),
    ),
  }));
}

function applyFiltersToIndexedProducts(
  products: Array<IndexedProduct>,
  filters: Filters,
): Array<IndexedProduct> {
  return products.filter(product => {
    for (const [filterName, filterValue] of Object.entries(filters)) {
      if (
        filterValue !== null &&
        ((filterName === 'type' && product.typeId !== filterValue) ||
          (filterName === 'activeSubstance' && !product.activeSubstanceIds.includes(filterValue)) ||
          (filterName === 'crop' && !product.cropIds.includes(filterValue)) ||
          (filterName.startsWith('problem-') && !product.problemIds.includes(filterValue)))
      ) {
        return false;
      }
    }
    return true;
  });
}

const ProductListPage = (): React.ReactElement => {
  if (HIDDEN_FEATURES.includes('products')) {
    return <Error404Page />;
  }

  const { t } = useTranslation();

  const data: QueryData = useStaticQuery(
    graphql`
      {
        allSanityProduct(sort: { fields: name }) {
          nodes {
            ...Product
          }
        }
        allSanityProductType {
          nodes {
            _id
            name
            slug {
              current
            }
            group {
              _id
              name
              otherProductType {
                _id
                name
              }
            }
          }
        }
        allSanityActiveSubstance {
          nodes {
            _id
            name
            slug {
              current
            }
          }
        }
        allSanityCrop {
          nodes {
            _id
            name
            slug {
              current
            }
            group {
              _id
              name
            }
          }
        }
        allSanityProblem {
          nodes {
            _id
            name
            slug {
              current
            }
            scientificName
            group {
              _id
              name
              category {
                _id
              }
            }
          }
        }
        allSanityProblemCategory {
          nodes {
            _id
            name
            slug {
              current
            }
          }
        }
        sanityProductsListPage {
          disclaimer
          disclaimerArticle {
            title
            slug {
              current
            }
          }
        }
      }
    `,
  );
  const {
    allSanityProduct: { nodes: allProducts },
    allSanityProductType: { nodes: allProductTypes },
    allSanityActiveSubstance: { nodes: allActiveSubstances },
    allSanityCrop: { nodes: allCrops },
    allSanityProblem: { nodes: allProblems },
    allSanityProblemCategory: { nodes: allProblemCategories },
    sanityProductsListPage: productsListPage,
  } = data;

  const allListedProducts = allProducts.filter(product => !product.unlisted);

  const productsById = mapBy(allListedProducts, '_id');

  const productTypesById = mapBy(allProductTypes, '_id');
  const allProductTypeGroups = sortBy(
    uniqBy(
      allListedProducts.map(product => (productTypesById[product.type._id] as ProductType).group),
      '_id',
    ),
    'name',
  );

  const activeSubstancesById = mapBy(allActiveSubstances, '_id');

  const cropsById = mapBy(allCrops, '_id');
  const allCropGroups = sortBy(
    uniqBy(
      flatten(
        allListedProducts.map(
          product =>
            product.applications?.map(
              application => (cropsById[application.crop._id] as Crop).group,
            ) || [],
        ),
      ),
      '_id',
    ),
    'name',
  );

  const problemsById = mapBy(allProblems, '_id');
  const allProblemGroups = sortBy(
    uniqBy(
      flatten(
        allListedProducts.map(product =>
          product.applications
            ? flatten(
                product.applications.map(application =>
                  application.cropApplications
                    ? application.cropApplications
                        .filter(cropApplication => !!cropApplication.problem)
                        .map(
                          cropApplication =>
                            (problemsById[cropApplication.problem._id] as Problem).group,
                        )
                    : [],
                ),
              )
            : [],
        ),
      ),
      '_id',
    ),
    'name',
  );

  const problemCategoriesById = mapBy(allProblemCategories, '_id');
  const allUsedProblemCategories = sortBy(
    uniqBy(
      allProblemGroups.map(group => problemCategoriesById[group.category._id] as ProblemCategory),
      '_id',
    ),
    'name',
  );

  const initialEmptyProblemFilters: { [key: string]: null } = {};
  for (const category of allUsedProblemCategories) {
    initialEmptyProblemFilters['problem-' + category._id] = null;
  }

  const [activeFilters, setActiveFilters] = useState<Filters>({
    type: null,
    activeSubstance: null,
    crop: null,
    ...initialEmptyProblemFilters,
  });

  function updateFilters(filterName: string, value: string | null) {
    setActiveFilters({
      ...activeFilters,
      [filterName]: value,
    });
    resetPagination();
  }

  const indexedProducts = indexProducts(allListedProducts);
  const filteredProducts = applyFiltersToIndexedProducts(indexedProducts, activeFilters).map(
    indexedProduct => productsById[indexedProduct.id] as Product,
  );

  const typeIdsForFilter = uniq(
    applyFiltersToIndexedProducts(indexedProducts, { ...activeFilters, type: null }).map(
      indexedProduct => indexedProduct.typeId,
    ),
  );
  const activeSubstanceIdsForFilter = uniq(
    flatten(
      applyFiltersToIndexedProducts(indexedProducts, {
        ...activeFilters,
        activeSubstance: null,
      }).map(indexedProduct => indexedProduct.activeSubstanceIds),
    ),
  );
  const cropIdsForFilter = uniq(
    flatten(
      applyFiltersToIndexedProducts(indexedProducts, {
        ...activeFilters,
        crop: null,
      }).map(indexedProduct => indexedProduct.cropIds),
    ),
  );

  const {
    currentPage,
    currentPageItems: currentPageProducts,
    getPageUrlPart,
    resetPagination,
    renderPagination,
  } = usePagination(filteredProducts, 10);

  // Get initial filters state from url
  const hasRunOnLoadEffect = useOnLoadEffect(() => {
    let initialTypeId = null;
    let initialActiveSubId = null;
    let initialCropId = null;
    const initialProblemFilters: Filters = {};

    const productTypesBySlug = mapBy(allProductTypes, type => type.slug.current);
    const activeSubstancesBySlug = mapBy(allActiveSubstances, activeSub => activeSub.slug.current);
    const cropsBySlug = mapBy(allCrops, crop => crop.slug.current);
    const problemsBySlug = mapBy(allProblems, problem => problem.slug.current);

    const currentUrl = new URLSearchParams(window.location.search);
    const urlTypeSlugEncoded = currentUrl.get('type');
    const urlActiveSubSlugEncoded = currentUrl.get('activeSubstance');
    const urlCropSlugEncoded = currentUrl.get('crop');

    if (urlTypeSlugEncoded) {
      const initialType = productTypesBySlug[decodeURIComponent(urlTypeSlugEncoded)];
      initialTypeId = initialType ? initialType._id : null;
    }
    if (urlActiveSubSlugEncoded) {
      const initialActiveSub = activeSubstancesBySlug[decodeURIComponent(urlActiveSubSlugEncoded)];
      initialActiveSubId = initialActiveSub ? initialActiveSub._id : null;
    }
    if (urlCropSlugEncoded) {
      const initialCrop = cropsBySlug[decodeURIComponent(urlCropSlugEncoded)];
      initialCropId = initialCrop ? initialCrop._id : null;
    }

    for (const category of allUsedProblemCategories) {
      const urlProblemSlugEncoded = currentUrl.get(category.slug.current);
      const problem = urlProblemSlugEncoded
        ? problemsBySlug[decodeURIComponent(urlProblemSlugEncoded)]
        : null;
      initialProblemFilters['problem-' + category._id] =
        problem && problem.group.category._id === category._id ? problem._id : null;
    }
    setActiveFilters({
      type: initialTypeId,
      activeSubstance: initialActiveSubId,
      crop: initialCropId,
      ...initialProblemFilters,
    });
  });

  // Update url on filters state change
  useUpdateUrlFromFilters(() => {
    const type = activeFilters.type ? productTypesById[activeFilters.type] : null;
    const typeSlugEncoded = type ? encodeURIComponent(type.slug.current) : null;
    const activeSub = activeFilters.activeSubstance
      ? activeSubstancesById[activeFilters.activeSubstance]
      : null;
    const activeSubSlugEncoded = activeSub ? encodeURIComponent(activeSub.slug.current) : null;
    const crop = activeFilters.crop ? cropsById[activeFilters.crop] : null;
    const cropSlugEncoded = crop ? encodeURIComponent(crop.slug.current) : null;

    const problemsUrlParts = [];
    for (const [filterName, filter] of Object.entries(activeFilters)) {
      if (filterName.startsWith('problem-') && filter !== null) {
        const problemCategoryId = filterName.substr(filterName.indexOf('-') + 1);
        const problemCategory = problemCategoriesById[problemCategoryId] as ProblemCategory;
        const problem = problemsById[filter] as Problem;
        problemsUrlParts.push([
          encodeURIComponent(problemCategory.slug.current),
          encodeURIComponent(problem.slug.current),
        ] as const);
      }
    }

    return [
      ['type', typeSlugEncoded] as const,
      ['activeSubstance', activeSubSlugEncoded] as const,
      ['crop', cropSlugEncoded] as const,
      ...sortBy(problemsUrlParts, ([categorySlug]) => categorySlug),
      getPageUrlPart(),
    ];
  }, [
    ...sortBy(Object.entries(activeFilters), ([key]) => key).map(([, value]) => value),
    currentPage,
  ]);

  return (
    <PageLayout
      hasFilters
      title={t('products_page.title', 'Products')}
      sideBarChildren={
        <div className={filterStyles.box}>
          <span className={filterStyles.blueBordersLabel}>
            {t('product_filters.title', 'Filter By')}
          </span>
          {hasRunOnLoadEffect && (
            <div className={filterStyles.container}>
              <Autocomplete
                className={
                  filterStyles.input + (activeFilters.type ? ' ' + filterStyles.inputSelected : '')
                }
                classes={{
                  listbox: filterStyles.selectListbox,
                  groupUl: filterStyles.selectGroupUl,
                  option: filterStyles.selectOption,
                }}
                disableClearable={!activeFilters.type}
                disabled={typeIdsForFilter.length === 0}
                options={sortBy(
                  typeIdsForFilter.map(typeId => productTypesById[typeId] as ProductType),
                  [
                    type => type.group.name,
                    type => (type.group.otherProductType?._id === type._id ? 1 : 0),
                    'name',
                  ],
                ).map(type => type._id)}
                groupBy={
                  allProductTypeGroups.length > 1
                    ? typeId => {
                        return (productTypesById[typeId] as ProductType).group.name;
                      }
                    : undefined
                }
                getOptionLabel={typeId => {
                  if (!typeId) {
                    return '';
                  }
                  return (productTypesById[typeId] as ProductType).name;
                }}
                renderInput={params => (
                  <TextField {...params} label={t('product_filters.type_label', 'Product type')} />
                )}
                value={activeFilters.type || undefined}
                onChange={(_, value) => updateFilters('type', value || null)}
              />
              <Autocomplete
                className={
                  filterStyles.input +
                  (activeFilters.activeSubstance ? ' ' + filterStyles.inputSelected : '')
                }
                classes={{
                  listbox: filterStyles.selectListbox,
                  groupUl: filterStyles.selectGroupUl,
                  option: filterStyles.selectOption,
                }}
                disableClearable={!activeFilters.activeSubstance}
                disabled={activeSubstanceIdsForFilter.length === 0}
                options={sortBy(
                  activeSubstanceIdsForFilter.map(
                    activeSubstanceId => activeSubstancesById[activeSubstanceId] as ActiveSubstance,
                  ),
                  'name',
                ).map(activeSubstance => activeSubstance._id)}
                getOptionLabel={activeSubstanceId => {
                  if (!activeSubstanceId) {
                    return '';
                  }
                  return (activeSubstancesById[activeSubstanceId] as ActiveSubstance).name;
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('product_filters.active_substance_label', 'Active Substance')}
                  />
                )}
                value={activeFilters.activeSubstance || undefined}
                onChange={(_, value) => updateFilters('activeSubstance', value || null)}
              />
              <Autocomplete
                className={
                  filterStyles.input + (activeFilters.crop ? ' ' + filterStyles.inputSelected : '')
                }
                classes={{
                  listbox: filterStyles.selectListbox,
                  groupUl: filterStyles.selectGroupUl,
                  option: filterStyles.selectOption,
                }}
                disableClearable={!activeFilters.crop}
                disabled={cropIdsForFilter.length === 0}
                options={sortBy(
                  cropIdsForFilter.map(cropId => cropsById[cropId] as Crop),
                  [crop => crop.group.name, 'name'],
                ).map(crop => crop._id)}
                groupBy={
                  allCropGroups.length > 1
                    ? cropId => {
                        return (cropsById[cropId] as Crop).group.name;
                      }
                    : undefined
                }
                getOptionLabel={cropId => {
                  if (!cropId) {
                    return '';
                  }
                  return (cropsById[cropId] as Crop).name;
                }}
                renderInput={params => (
                  <TextField {...params} label={t('product_filters.crop_label', 'Crop')} />
                )}
                value={activeFilters.crop || undefined}
                onChange={(_, value) => updateFilters('crop', value || null)}
              />

              {!HIDDEN_FEATURES.includes('problem_filters')
                ? allUsedProblemCategories.map(problemCategory => {
                    const problemFilterName = 'problem-' + problemCategory._id;
                    const problemIdsForFilter = uniq(
                      flatten(
                        applyFiltersToIndexedProducts(indexedProducts, {
                          ...activeFilters,
                          [problemFilterName]: null,
                        }).map(indexedProduct => indexedProduct.problemIds),
                      ),
                    ).filter(
                      problemId =>
                        (problemsById[problemId] as Problem).group.category._id ===
                        problemCategory._id,
                    );

                    return (
                      <Autocomplete
                        key={problemCategory._id}
                        className={
                          filterStyles.input +
                          (activeFilters[problemFilterName] ? ' ' + filterStyles.inputSelected : '')
                        }
                        classes={{
                          listbox: filterStyles.selectListbox,
                          groupUl: filterStyles.selectGroupUl,
                          option: filterStyles.selectOption,
                        }}
                        disableClearable={!activeFilters[problemFilterName]}
                        disabled={problemIdsForFilter.length === 0}
                        options={sortBy(
                          problemIdsForFilter
                            .map(problemId => problemsById[problemId] as Problem)
                            .filter(problem => problem.group.category._id === problemCategory._id),
                          [problem => problem.group.name, getProblemCommonAndOrScientificName],
                        ).map(problem => problem._id)}
                        groupBy={
                          allProblemGroups.filter(
                            group => group.category._id === problemCategory._id,
                          ).length > 1
                            ? problemId => {
                                return (problemsById[problemId] as Problem).group.name;
                              }
                            : undefined
                        }
                        getOptionLabel={problemId => {
                          if (!problemId) {
                            return '';
                          }

                          return getProblemCommonAndOrScientificName(
                            problemsById[problemId] as Problem,
                          );
                        }}
                        renderInput={params => (
                          <TextField {...params} label={problemCategory.name} />
                        )}
                        value={activeFilters[problemFilterName] || undefined}
                        onChange={(_, value) => updateFilters(problemFilterName, value || null)}
                      />
                    );
                  })
                : []}
            </div>
          )}
        </div>
      }
      searchBox={
        <SearchBox<never, never>
          contentType="product"
          withTypoCorrection
          placeholderLong={
            t(
              'product_page.search_box.placeholder_long',
              'Search products by name, active substance, problem, crop or type',
            ) as string
          }
          minSearchTextChars={3}
          entriesGroups={[
            {
              matchFieldConfigs: {
                activeSubtance: t(
                  'product_page.search_box.match_location.active_substance',
                  'Active Substance',
                ),
                problem: t('product_page.search_box.match_location.problem', 'Problem'),
                crop: t('product_page.search_box.match_location.crop', 'Crop'),
                type: t('product_page.search_box.match_location.type', 'Product type'),
              },
              entries: sortBy(allListedProducts, 'name').map(product => ({
                name: product.name,
                url: '/products/' + product.slug.current,
                matchFields: [
                  {
                    name: 'activeSubtance',
                    values: product.activeSubstanceInfos?.map(
                      ({ activeSubstance }) => activeSubstance.name,
                    ),
                  },
                  {
                    name: 'problem',
                    values: uniq(
                      flatten(
                        product.applications
                          ?.map(({ cropApplications }) => cropApplications)
                          .filter(
                            (
                              ca,
                            ): ca is {
                              problem: {
                                _id: ID;
                                name: string;
                              };
                            }[] => !!ca,
                          ) || [],
                      )
                        .map(cropApplication => cropApplication.problem.name)
                        .filter(Boolean),
                    ),
                  },
                  {
                    name: 'crop',
                    values: product.applications?.map(({ crop }) => crop.name).filter(Boolean),
                  },
                  {
                    name: 'type',
                    values: [product.type.name],
                  },
                ].filter(
                  (matchField): matchField is MatchField =>
                    !!matchField.values && matchField.values.length > 0,
                ),
              })),
            },
          ]}
          noResultsMessage={t(
            'products_page.search_box.no_search_results_text',
            'No products match the searched keyword.',
          )}
          renderNamePart={convertRToSuperscript}
        ></SearchBox>
      }
    >
      <SEO title={t('products_page.title', 'Products')} urlPath="/products" />
      {hasRunOnLoadEffect && (
        <div className={styles.container}>
          {currentPageProducts.map(product => (
            <Link
              key={product._id}
              className={styles.cardLinkWrapper}
              to={`/products/${product.slug.current}`}
            >
              <ProductCard product={product}></ProductCard>
            </Link>
          ))}
        </div>
      )}

      {renderPagination()}
      {!!productsListPage && !!productsListPage.disclaimer && (
        <div className={styles.disclaimerContainer}>
          <div className={styles.leftSide}>
            <h4 className={styles.subTitle}>{t('product_page.disclaimer.title', 'Disclaimer')}</h4>
          </div>
          <div className={styles.rightSide}>
            <p>{productsListPage.disclaimer}</p>
            {!!productsListPage.disclaimerArticle && (
              <GenericLink
                to={'/articles/' + productsListPage.disclaimerArticle.slug.current}
                className={styles.disclaimerLink}
              >
                {productsListPage.disclaimerArticle.title} &gt;
              </GenericLink>
            )}
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default ProductListPage;
